<div *transloco="let t">
	<vex-page-layout mode="card">
		<vex-page-layout-header class="pb-16 flex flex-col items-start justify-center">
			<div>
				<h1 class="title mt-0 mb-1">{{ t('billing.billingUnit') }}</h1>
				<vex-breadcrumbs [crumbs]="breadCrumbService.crumbs"></vex-breadcrumbs>
			</div>
		</vex-page-layout-header>
		<vex-page-layout-content class="-mt-6">
			<ara-tenant-detail-cards></ara-tenant-detail-cards>
			<ng-template #billingsContent>
				@if (billingContentReady()) {
					<!-- Periods Table Section -->
					<div class="periods-section pt-2">
						<div class="section-header flex items-center gap-2 m-4">
							<mat-icon fontSet="material-symbols-outlined" class="text-primary">calendar_month</mat-icon>
							<h3 class="text-lg font-medium m-0">{{ t('billing.periods') }}</h3>
							@if (visibleCardCount > 1) {
								<button (click)="toggleSort()" class="ml-auto flex items-center gap-1" mat-button>
									<mat-icon fontSet="material-symbols-outlined">
										{{ sortDirection() === 'desc' ? 'south' : 'north' }}
									</mat-icon>
									<span>{{ sortDirection() === 'desc' ? 'neueste zuerst' : ' letzte zuerst' }}</span>
								</button>
							}
						</div>
					</div>
					<ng-container>
						<div class="flex flex-wrap">
							@if (visibleCardCount === 0) {
								<div class="no-activated-periods">
									{{ t('billing.noActivatedBillingPeriods') }}
								</div>
							}
							@for (period of periods(); track period.exportId) {
								@if (
									period.customerAccessStatus === CustomerAccessStatus.ACTIVATED ||
									period.customerAccessStatus === CustomerAccessStatus.SUBMITTED
								) {
									<mat-card class="relative m-4 min-h-36 w-96">
										@if (period.customerAccessStatus === CustomerAccessStatus.SUBMITTED) {
											<div class="submitted-overlay absolute inset-0 pointer-events-none"></div>
										}
										@if (period.customerAccessStatus == CustomerAccessStatus.SUBMITTED) {
											<div class="submitted-lock">
												<mat-icon fontSet="material-symbols-outlined">lock</mat-icon>
												<div class="submitted-lock__date">
													{{ dateUtil.setDateFormatByActiveLang(period.validFrom, '') }}
													- {{ dateUtil.setDateFormatByActiveLang(period.validTo, '') }}
												</div>
												<div class="submitted-lock__text">{{ t('billing.periodCompleted') }}</div>
											</div>
										}

										<mat-card-content
											class="period-card__content"
											[class.submitted-content]="period.customerAccessStatus === CustomerAccessStatus.SUBMITTED">
											<div class="flex flex-col gap-2">
												<div class="period-card__header">
													<mat-icon
														class="period-card__leading-icon"
														fontSet="material-symbols-outlined"
														[class.text-primary]="period.customerAccessStatus === CustomerAccessStatus.ACTIVATED"
														[class.text-gray-400]="period.customerAccessStatus === CustomerAccessStatus.SUBMITTED">
														calendar_month
													</mat-icon>

													<div class="period-card__actions">
														<button
															mat-icon-button
															[matTooltip]="t('billing.notes')"
															(click)="openNotes(period)"
															[class.text-black]="period.customerAccessStatus === CustomerAccessStatus.ACTIVATED"
															[class.text-gray-400]="period.customerAccessStatus === CustomerAccessStatus.SUBMITTED">
															<mat-icon
																fontSet="material-symbols-outlined"
																[matBadge]="noteCounts()[period.exportId]"
																matBadgeColor="primary"
																[matBadgeHidden]="!noteCounts()[period.exportId]">
																comment
															</mat-icon>
														</button>

														<button
															mat-icon-button
															[matTooltip]="t('billing.costEntry')"
															(click)="navigateToCosts(period)"
															[class.text-black]="period.customerAccessStatus === CustomerAccessStatus.ACTIVATED"
															[class.text-gray-400]="period.customerAccessStatus === CustomerAccessStatus.SUBMITTED">
															<mat-icon fontSet="material-symbols-outlined">euro</mat-icon>
														</button>

														<button
															mat-icon-button
															[matTooltip]="t('billing.settings')"
															(click)="navigateToSettings(period)"
															[class.text-black]="period.customerAccessStatus === CustomerAccessStatus.ACTIVATED"
															[class.text-gray-400]="period.customerAccessStatus === CustomerAccessStatus.SUBMITTED">
															<mat-icon fontSet="material-symbols-outlined">settings</mat-icon>
														</button>

														<button
															mat-icon-button
															[matTooltip]="t('billing.more')"
															[matMenuTriggerFor]="cardPeriodActions"
															[class.text-black]="period.customerAccessStatus === CustomerAccessStatus.ACTIVATED">
															<mat-icon fontSet="material-symbols-outlined">more_vert</mat-icon>
														</button>

														<mat-menu #cardPeriodActions="matMenu">
															<button mat-menu-item [disabled]="true">
																<mat-icon fontSet="material-symbols-outlined" color="primary">download</mat-icon>
																<div>{{ t('billing.downloadPreliminaryData') }}</div>
															</button>
														</mat-menu>
													</div>
												</div>

												@if (period.customerAccessStatus == CustomerAccessStatus.ACTIVATED) {
													<div class="text-sm period-date">
														{{ dateUtil.setDateFormatByActiveLang(period.validFrom, '') }}
														- {{ dateUtil.setDateFormatByActiveLang(period.validTo, '') }}
													</div>
												}

												@if (period.customerAccessStatus == CustomerAccessStatus.ACTIVATED) {
													<div class="mt-4 w-full">
														<ara-drag-to-complete (completed)="openCompletionDialog(period)"></ara-drag-to-complete>
													</div>
												}
											</div>
										</mat-card-content>
									</mat-card>
								}
							}
						</div>
					</ng-container>
				} @else {
					<div @fadeInUp class="flex flex-row justify-center">
						<mat-spinner color="primary"></mat-spinner>
					</div>
				}
			</ng-template>
			<div class="card overflow-auto">
				@if (visibleCardCount === 0) {
					<ng-container [ngTemplateOutlet]="billingsContent"></ng-container>
				} @else {
					<mat-tab-group
						[mat-stretch-tabs]="false"
						[selectedIndex]="selectedTabIndex()"
						(selectedIndexChange)="selectedTabIndex.set($event)"
						preserveContent="false">
						<mat-tab label="{{ t('billing.billings') }}" [disabled]="!finishedLoading()">
							<ng-template matTabContent>
								<ng-container [ngTemplateOutlet]="billingsContent"></ng-container>
							</ng-template>
						</mat-tab>
						<mat-tab label="{{ t('billing.costs') }}" [disabled]="!finishedLoading()">
							<ng-template matTabContent>
								<div class="h-full min-h-[50vh]">
									<vex-costs [billingId]="billingId" [periods]="periods()" (navigateToAllocations)="selectedTabIndex.set(2)"></vex-costs>
								</div>
							</ng-template>
						</mat-tab>
						<mat-tab label="{{ t('billing.settings') }}" [disabled]="!finishedLoading()">
							<ng-template matTabContent>
								<vex-basic-data #basicData [billingId]="billingId" [periods]="periods()"></vex-basic-data>
							</ng-template>
						</mat-tab>
					</mat-tab-group>
				}
			</div>
		</vex-page-layout-content>
	</vex-page-layout>
</div>
